/* eslint-disable @next/next/no-img-element */
import AiUsageDownloadComponent from 'components/feature/AiUsage/AiUsageDownloadComponent'
import MistralAIPartnership from 'components/feature/AiUsage/MistralAIPartnership'
import WhatSetsUsApartComponent from 'components/feature/AiUsage/WhatSetsUsApartComponent'
import Meta from 'components/modules/SEO/Meta'
import { parseLocale } from 'lib/constants/localization'
import _useTranslation from 'lib/hooks/_useTranslation'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import React from 'react'
import { Body1, Body2, ButtonText, Headline1, Headline2, Headline3 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import Background from 'assets/ai-usage/bg.png'
import NewButton from 'components/elements/NewButton'
import Stack from 'components/elements/Stack'
import VideoPlayer from 'components/modules/Players/VideoPlayer'
import PhoneOverlay from 'assets/ai-usage/phone.png'
import AdvantagesContainer from 'components/feature/AiUsage/AdvantagesContainer'
import { getAIVideo } from 'lib/constants/knowunityAIUsp'
import { useRouter } from 'next/router'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import NextImage from 'components/elements/NextImage'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'

const Box = styled.div`
    display: grid;
    grid-template-columns: 1fr;
`

const Container = styled.div`
    position: relative;
    background-color: ${theme.colors.lighterGrayBackground};
    background-size: cover;
    background-repeat: no-repeat;
    padding: 50px;
    .text-container * {
        color: ${theme.colors.white};
    }

    .phone-overlay {
        position: absolute;
        top: 0;
        right: 0;
        width: 55%;
        height: auto;
        z-index: 1;

        @media (max-width: 1050px) {
            width: 60%;
        }
        @media (max-width: 750px) {
            width: 90%;
        }
    }

    section {
        position: relative;
        z-index: 2;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 50px;
        row-gap: 100px;
        align-items: start;
        padding-bottom: 50px;
        button {
            width: 250px;
        }
        @media (max-width: 1050px) {
            grid-template-columns: 1fr;
            gap: 10px;
            padding-top: 250px;
        }
    }
    @media (max-width: 1050px) {
        padding: 25px;
    }

    .text-container {
        display: flex;
        flex-direction: column;
        gap: 30px;
        padding: 35px 0;
        width: 85%;
        h1 {
            margin: 0;
            padding: 0;
        }
        @media (max-width: 750px) {
            width: 100%;
            * {
                text-align: center;
            }
            button {
                width: 100%;
            }
        }
    }
    .video-wrapper {
        overflow: hidden;
        background: transparent;
        border-top: 3px solid #374151;
        border-radius: 18px;
    }
    video {
        position: relative;
        border: 3px solid #374151;
        top: -25px;
    }
`

const AiUsage = () => {
    const { t } = _useTranslation('aiUsage')
    const { t: tCommon } = _useTranslation('common')
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const md = useMedia(deviceSize.md)

    return (
        <Box>
            <Meta title={t('aiUsage/title')} description={t('aiUsage/description')} />
            <Container
                style={{
                    backgroundImage: Background?.src ? `url(${Background.src ?? ''}), url(${Background.src ?? ''})` : '',
                }}
            >
                {PhoneOverlay?.src && (
                    <NextImage
                        src={PhoneOverlay.src}
                        width={PhoneOverlay.width}
                        height={PhoneOverlay.height}
                        alt=""
                        className="phone-overlay"
                        quality={100}
                    />
                )}
                <section>
                    <div className="text-container">
                        <Headline1 fontSize={60} fontWeight={700} fontFamily="var(--font-poppins)">
                            {t('aiUsage/title')}
                        </Headline1>
                        <Headline2 fontSize={24} fontWeight={600} fontFamily="var(--font-poppins)">
                            {t('aiUsage/subtitle')}
                        </Headline2>
                        <Body2 fontSize={16} fontWeight={400} fontFamily="var(--font-inter)">
                            {t('aiUsage/description')}
                        </Body2>
                        <NewButton
                            color={theme.colors.signalPurple}
                            borderRadius={'100px'}
                            width={250}
                            height={60}
                            onClick={() => redirectToAppDownloadUrlAsync('dynamic', 'knowunity-ai')}
                        >
                            <ButtonText
                                fontFamily="var(--font-inter)"
                                fontWeight={600}
                                fontSize={18}
                                color={theme.colors.white}
                            >
                                {tCommon('common/conversionCTAButton')}
                            </ButtonText>
                        </NewButton>
                    </div>
                    <div />

                    <div className="text-container">
                        <Headline2 fontSize={24} fontWeight={600}>
                            {t('aiUsage/ourCommitment')}
                        </Headline2>
                        <Body2 fontSize={16} fontWeight={400} fontFamily="var(--font-inter)">
                            {t('aiUsage/ourCommitmentDescription')}
                        </Body2>
                    </div>

                    <div className="text-container">
                        <Headline2 fontSize={24} fontWeight={600}>
                            {t('aiUsage/theKnowunityDifference')}
                        </Headline2>
                        <Body2 fontSize={16} fontWeight={400} fontFamily="var(--font-inter)">
                            {t('aiUsage/theKnowunityDifferenceDescription')}
                        </Body2>
                    </div>
                    <Headline2
                        fontWeight={700}
                        fontFamily="var(--font-poppins)"
                        color={theme.colors.white}
                        fontSize={36}
                        textAlign={md ? 'center' : 'left'}
                        marginBottom={30}
                        style={{
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t('aiUsage/theKnowunityDifference')}
                    </Headline2>
                </section>
                <AdvantagesContainer isReversed noPadding style={{ padding: md ? '0' : '50px 0' }}>
                    <section className="content-section">
                        <div className="text-section">
                            <Stack gutter={10} className="text-section-inner">
                                <Body1
                                    className="tagline"
                                    fontFamily="var(--font-inter)"
                                    color={theme.colors.businessBlue}
                                    fontSize={15}
                                    fontWeight={800}
                                >
                                    {t('aiUsage/myAiTitle')}
                                </Body1>
                                <Headline3
                                    fontFamily="var(--font-inter)"
                                    fontWeight={700}
                                    fontSize={32}
                                    color={theme.colors.white}
                                >
                                    {t('aiUsage/getHelpfulVisualsFromAI')}
                                </Headline3>
                                <Body1
                                    fontFamily="var(--font-open-sans)"
                                    fontWeight={400}
                                    fontSize={16}
                                    color={'#F3F4F699'}
                                    marginBottom={20}
                                >
                                    {t('aiUsage/getHelpfulVisualsFromAIDescription')}
                                </Body1>
                            </Stack>
                            <NewButton
                                className="hidden-mobile"
                                color={theme.colors.businessBlue}
                                borderRadius={'100px'}
                                width={250}
                                height={60}
                                onClick={() => redirectToAppDownloadUrlAsync('dynamic', 'knowunity-ai')}
                            >
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    fontSize={18}
                                    color={theme.colors.white}
                                >
                                    {tCommon('common/conversionCTAButton')}
                                </ButtonText>
                            </NewButton>
                        </div>
                        <div className="media-section">
                            <VideoPlayer
                                source={getAIVideo(parsedLocale.interfaceLanguageCode, 1)}
                                width={212}
                                height={440}
                                autoPlay
                                muted
                                loop
                                border="3px solid #374151"
                            />
                            <NewButton
                                className="hidden-desktop"
                                color={theme.colors.businessBlue}
                                borderRadius={'100px'}
                                width={250}
                                height={60}
                                onClick={() => redirectToAppDownloadUrlAsync('dynamic', 'knowunity-ai')}
                            >
                                <ButtonText
                                    fontFamily="var(--font-inter)"
                                    fontWeight={600}
                                    fontSize={18}
                                    color={theme.colors.white}
                                >
                                    {tCommon('common/conversionCTAButton')}
                                </ButtonText>
                            </NewButton>
                        </div>
                    </section>
                </AdvantagesContainer>
            </Container>

            <WhatSetsUsApartComponent />
            <AiUsageDownloadComponent />
            <MistralAIPartnership />
        </Box>
    )
}

export const getStaticProps: GetStaticProps = async (props) => ({
    props: {
        ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common', 'aiUsage', 'knows'])),
    },
})

export default AiUsage
